import { Box, Card, CardActionArea, CircularProgress, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useRef, useState } from 'react';
import useAssets from '@/infrastructure/hooks/useAssets';
import { ASSET_PATHS, LEAD_ATTACHMENT_SUPPORTED_FORMATS } from '@lemontaps/lib/constants';
import SettingsMenu, { MenuItemType } from '@/components/SettingsMenu';
import {
  DeleteOutlined,
  Download,
  InfoOutlined,
  InsertDriveFileOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import LtDialog from '@/components/LtDialog';
import { useTranslation } from 'react-i18next';
import useLtNotifications from '@/infrastructure/notifications/useLtNotifications';
import { downloadFile } from '@/infrastructure/helper';
type Props = {
  value: { id: string; location: string }[];
  onChange: (value: { id: string; location: string }[]) => void;
};

const isImage = (asset: { id: string; location: string }) => {
  return (
    asset.location.endsWith('.png') ||
    asset.location.endsWith('.jpg') ||
    asset.location.endsWith('.jpeg') ||
    asset.location.endsWith('.gif') ||
    asset.location.endsWith('.svg') ||
    asset.location.endsWith('.webp')
  );
};

const AttchmentContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box sx={{ position: 'relative', paddingBottom: '100%' }}>
      <Card sx={{ position: 'absolute', inset: 0 }}>{children}</Card>
    </Box>
  );
};

export const Attachments = ({ value, onChange }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { notify } = useLtNotifications();
  const { t } = useTranslation();
  const [attachmentToDelete, setAttachmentToDelete] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const { allocateAssets } = useAssets();

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const assets = await allocateAssets(files, 3, ASSET_PATHS.LeadAttachment);
      onChange([...value, ...assets.map(asset => ({ id: asset.id, location: asset.location }))]);
    } catch (error) {
      notify.error(
        t('leadGen.attachments.unsupportedFile', {
          supportedFormats: LEAD_ATTACHMENT_SUPPORTED_FORMATS.map(format => format.ext).join(', '),
        }),
      );
    }
    setUploading(false);
  };

  return (
    <Box mt={1}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <AttchmentContainer>
            <CardActionArea
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AddIcon sx={{ width: '40%', height: '40%' }} />
            </CardActionArea>
          </AttchmentContainer>
        </Grid>
        {value.map(asset => (
          <Grid item xs={3} key={asset.id}>
            <Box sx={{ position: 'relative' }}>
              <AttchmentContainer>
                {isImage(asset) ? (
                  <Box
                    component='img'
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    src={asset.location}
                    alt='attachment'
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <InsertDriveFileOutlined sx={{ width: '30%', height: '30%' }} />
                  </Box>
                )}
              </AttchmentContainer>
              <Box sx={{ position: 'absolute', bottom: '-1rem', right: '-1rem' }}>
                <SettingsMenu
                  menuWidth='20rem'
                  iconButtonProps={{
                    color: 'primary',
                    size: 'small',
                    sx: ({ shadows }) => ({
                      bgcolor: 'white',
                      boxShadow: shadows[1],
                      '&:hover': {
                        backgroundColor: 'white',
                      },
                    }),
                  }}
                  menuItems={
                    [
                      isImage(asset) && {
                        icon: <VisibilityOutlined />,
                        label: 'Preview',
                        onClick: () => {
                          window.open(asset.location, '_blank');
                        },
                      },
                      {
                        icon: <Download />,
                        label: 'Download',
                        onClick: () => {
                          downloadFile(
                            asset.location,
                            `attachment.${asset.location.split('.').pop()}`,
                          );
                        },
                      },
                      {
                        icon: <DeleteOutlined />,
                        label: 'Delete',
                        onClick: () => {
                          setAttachmentToDelete(asset.id);
                        },
                      },
                    ].filter(Boolean) as MenuItemType[]
                  }
                />
              </Box>
            </Box>
          </Grid>
        ))}
        {uploading && (
          <Grid item xs={3}>
            <AttchmentContainer>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={30} />
              </Box>
            </AttchmentContainer>
          </Grid>
        )}
      </Grid>
      <input
        ref={inputRef}
        type='file'
        onChange={handleChange}
        multiple
        style={{ display: 'none' }}
      />
      <LtDialog
        title={t('requestDelete')}
        titleIcon={<InfoOutlined color='warning' />}
        open={!!attachmentToDelete}
        onClose={() => setAttachmentToDelete(null)}
        onCancel={() => setAttachmentToDelete(null)}
        onDelete={() => {
          onChange(value.filter(val => val.id !== attachmentToDelete));
          setAttachmentToDelete(null);
        }}
      >
        {t('genericDelete')}
      </LtDialog>
    </Box>
  );
};
